import React, { useMemo } from "react";
import { Divider, Typography } from "@mui/material";
import { Select, TextField } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { CompanyInvoiceSettings } from "hooks/useCompanySettings";
import { FormikProps } from "formik";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import { InvoicePaymentTerms } from "enums/InvoicePaymentTerms";
import { InvoiceDiscountType } from "enums/InvoiceDiscountType";
import { Inline } from "components/layout/reusableLayouts";
import { PaymentTermsLabels } from "utils/invoices/invoiceFormat";
import SettingsRow from "./SettingsRow";

const INVOICE_FORMATS = [
  "YYYY-MM-INCREMENT",
  "MM-DD-INCREMENT",
  "YYYY-INCREMENT",
  "INCREMENT",
];

interface InvoiceSectionProps {
  editMode: boolean;
  invoiceFormik: FormikProps<CompanyInvoiceSettings>;
  uploading: boolean;
}

// Helper function to generate the example invoice number
const generateExampleInvoiceNumber = (
  format: string,
  prefix: string,
  suffix: string,
  incrementLength: number,
) => {
  const currentYear = new Date().getFullYear().toString();
  const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, "0");
  const currentDay = new Date().getDate().toString().padStart(2, "0");
  const increment = "1".padStart(incrementLength, "0");

  return `${prefix ? `${prefix}-` : ""}${format
    .replace("YYYY", currentYear)
    .replace("MM", currentMonth)
    .replace("DD", currentDay)
    .replace("INCREMENT", increment)}${suffix ? `-${suffix}` : ""}`;
};

/**
 * The rows this tab renders, each entry saying whether that row has a
 * description, so a loading skeleton can match what replaces it. Only the
 * example invoice number goes without one.
 */
export const INVOICE_SKELETON_ROWS = [
  false,
  true,
  true,
  true,
  true,
  true,
  true,
];

// Scrolling over a focused number input otherwise changes the saved value
// silently. The same guard sits on every other number field in the app.
const blurOnWheel = (event: React.WheelEvent<HTMLInputElement>) =>
  event.currentTarget.blur();

const InvoiceSettings: React.FC<InvoiceSectionProps> = ({
  editMode,
  invoiceFormik,
  uploading,
}) => {
  const { t } = useTranslation();

  const sanitizeIncrementLength = (value: string) => {
    // Ensure the value is a whole number between 2 and 10
    const num = Math.floor(Number(value) || 0);
    return Math.max(2, Math.min(10, num));
  };

  const handleIncrementLengthChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const sanitizedValue = sanitizeIncrementLength(e.target.value);
    invoiceFormik.setFieldValue("incrementLength", sanitizedValue);
  };

  const sanitizeInvoiceTax = (value: string) => {
    // Ensure the value is a non-negative number
    const num = parseFloat(value);
    return Math.max(0, isNaN(num) ? 0 : num);
  };

  const handleInvoiceTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInvoiceTax(e.target.value);
    invoiceFormik.setFieldValue("defaultTax", sanitizedValue);
  };

  const exampleInvoiceNumber = useMemo(() => {
    const { format, prefix, suffix, incrementLength } = invoiceFormik.values;

    return generateExampleInvoiceNumber(
      typeof format === "string" ? format : "YYYY-MM-INCREMENT",
      typeof prefix === "string" ? prefix : "",
      typeof suffix === "string" ? suffix : "",
      typeof incrementLength === "number" ? incrementLength : 6,
    );
  }, [invoiceFormik.values]);

  return (
    <>
      <SettingsRow label={t("Example Invoice Number")}>
        <Typography variant="body1">{exampleInvoiceNumber}</Typography>
      </SettingsRow>
      <Divider />

      <SettingsRow
        label={t("Invoice Format")}
        description={t("InvoiceFormatDescription")}
      >
        {editMode ? (
          <Select
            fullWidth
            size="md"
            clearable={false}
            aria-label={t("Invoice Format")}
            value={String(invoiceFormik.values.format ?? "")}
            options={INVOICE_FORMATS.map((option) => ({
              id: option,
              label: t(option),
            }))}
            onChange={(value) =>
              invoiceFormik.setFieldValue("format", value ?? "")
            }
          />
        ) : (
          <Typography variant="body1">{invoiceFormik.values.format}</Typography>
        )}
      </SettingsRow>
      <Divider />

      <SettingsRow
        label={t("Invoice Prefix")}
        description={t("InvoicePrefixDescription")}
      >
        {editMode ? (
          <TextField
            fullWidth
            size="md"
            aria-label={t("Invoice Prefix")}
            value={invoiceFormik.values.prefix}
            onChange={invoiceFormik.handleChange}
            name="prefix"
          />
        ) : (
          <Typography variant="body1">{invoiceFormik.values.prefix}</Typography>
        )}
      </SettingsRow>
      <Divider />

      <SettingsRow
        label={t("Invoice Suffix")}
        description={t("InvoiceSuffixDescription")}
      >
        {editMode ? (
          <TextField
            fullWidth
            size="md"
            aria-label={t("Invoice Suffix")}
            value={invoiceFormik.values.suffix}
            onChange={invoiceFormik.handleChange}
            name="suffix"
          />
        ) : (
          <Typography variant="body1">{invoiceFormik.values.suffix}</Typography>
        )}
      </SettingsRow>
      <Divider />

      <SettingsRow
        label={t("Increment Length")}
        description={t("IncrementLengthDescription")}
      >
        {editMode ? (
          <TextField
            fullWidth
            type="number"
            size="md"
            aria-label={t("Increment Length")}
            inputProps={{ onWheel: blurOnWheel }}
            value={invoiceFormik.values.incrementLength}
            onChange={handleIncrementLengthChange}
          />
        ) : (
          <Typography variant="body1">
            {invoiceFormik.values.incrementLength}
          </Typography>
        )}
      </SettingsRow>
      <Divider />

      <SettingsRow
        label={t("Default Payment Terms")}
        description={t("DefaultPaymentTermsDescription")}
      >
        {editMode ? (
          <Select
            fullWidth
            size="md"
            clearable={false}
            aria-label={t("Default Payment Terms")}
            value={String(invoiceFormik.values.defaultPaymentTerms ?? "")}
            options={Object.values(InvoicePaymentTerms).map((option) => ({
              id: option,
              label: PaymentTermsLabels[option],
            }))}
            onChange={(value) =>
              invoiceFormik.setFieldValue("defaultPaymentTerms", value ?? "")
            }
          />
        ) : (
          <Typography variant="body1">
            {
              PaymentTermsLabels[
                invoiceFormik.values.defaultPaymentTerms as InvoicePaymentTerms
              ]
            }
          </Typography>
        )}
      </SettingsRow>
      <Divider />

      <SettingsRow
        label={t("Default Tax Rate")}
        description={t("DefaultTaxRateDescription")}
      >
        {editMode ? (
          <Inline gap="var(--space-3)" align="stretch" wrap={false}>
            <Select
              size="md"
              clearable={false}
              fullWidth={false}
              width="88px"
              aria-label={`${t("Default Tax Rate")} ${t("Type")}`}
              value={String(invoiceFormik.values.defaultTaxType ?? "")}
              options={[
                { id: InvoiceDiscountType.PERCENTAGE, label: "%" },
                { id: InvoiceDiscountType.AMOUNT, label: "$" },
              ]}
              onChange={(value) =>
                invoiceFormik.setFieldValue("defaultTaxType", value ?? "")
              }
            />
            <TextField
              fullWidth
              size="md"
              type="number"
              aria-label={t("Default Tax Rate")}
              inputProps={{ onWheel: blurOnWheel }}
              name="defaultTax"
              value={invoiceFormik.values.defaultTax}
              onChange={handleInvoiceTaxChange}
              onBlur={invoiceFormik.handleBlur}
            />
          </Inline>
        ) : (
          <Typography variant="body1">
            {invoiceFormik.values.defaultTaxType ===
            InvoiceDiscountType.PERCENTAGE
              ? `${invoiceFormik.values.defaultTax}%`
              : `$${Number(invoiceFormik.values.defaultTax).toFixed(2)}`}
          </Typography>
        )}
      </SettingsRow>

      <LoadingWithoutModal open={uploading} />
    </>
  );
};

export default InvoiceSettings;
